export const getBadgeProps = (source_application) => {
  if (source_application?.toLowerCase().includes("rbfs")) {
    return { label: "RBFS", backgroundColor: "#007bff", textColor: "#ffffff" };
  } else {
    return { label: "MS2", backgroundColor: "#28a745", textColor: "#ffffff" };
  }
};

export const ALTERNATIVE_ANALYSIS_CATEGORY = "Alternative Analysis Model";
export const BASELINE_CATEGORY = "Baseline Model";

/** Shared Alternative Analysis / Baseline badge colors used across list, popup, and map panel. */
export const getCategoryBadgeProps = (modelCategory) => {
  if (modelCategory === ALTERNATIVE_ANALYSIS_CATEGORY) {
    return { label: "Alternative Analysis", bg: "#E8A87C", textColor: "#000" };
  }
  if (modelCategory === BASELINE_CATEGORY) {
    return { label: "Baseline", bg: "#00B4D8", textColor: "#fff" };
  }
  return null;
};

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when `content` is the RBFS package bucket (array/object keyed by storage GUID),
 * not a subfolder literally named "content" inside the model tree.
 */
export const looksLikePackageContent = (node) => {
  if (!node || typeof node !== "object") return false;
  if (Array.isArray(node)) {
    if (node.length === 0) return false;
    const first = node[0];
    if (first && typeof first === "object" && !Array.isArray(first)) {
      return Object.keys(first).some((k) => GUID_PATTERN.test(k));
    }
    return false;
  }
  const keys = Object.keys(node);
  return keys.length === 1 && GUID_PATTERN.test(keys[0]);
};

/**
 * Unwrap only the GUID bucket under package `content`, preserving model folder depth
 * (single or double model_name segments).
 */
export const extractContentUnderGuid = (contentNode) => {
  if (contentNode == null) return null;
  if (typeof contentNode === "string") return contentNode;

  if (Array.isArray(contentNode)) {
    if (contentNode.length === 0) return null;
    const bucket = contentNode[0];
    if (bucket && typeof bucket === "object" && !Array.isArray(bucket)) {
      const guidKey = Object.keys(bucket)[0];
      if (guidKey) return bucket[guidKey];
    }
    return contentNode;
  }

  if (typeof contentNode === "object") {
    const keys = Object.keys(contentNode);
    if (keys.length === 1 && GUID_PATTERN.test(keys[0])) {
      return contentNode[keys[0]];
    }
  }

  return contentNode;
};

export const findBy = (node, type = "content") => {
  if (node && typeof node === "object") {
    for (const key in node) {
      if (key === type) {
        const nextNode = node[key];
        if (nextNode == null || typeof nextNode !== "object") {
          return nextNode;
        }
        if (looksLikePackageContent(nextNode)) {
          return extractContentUnderGuid(nextNode);
        }
        const inner = findBy(nextNode, type);
        if (inner !== null) return inner;
        continue;
      }
      const result = findBy(node[key], type);
      if (result !== null) return result;
    }
  }
  return null;
};

// Main function to convert flat paths to nested structure
export const convertToNestedStructure = (flatStructure, key = "content") => {
  try {
    const content = findBy(flatStructure, key);
    if (!content) {
      console.warn(
        `"${key}" field is missing or invalid. Returning default structure.`,
      );
      return {
        name: "",
        children: [],
      };
    }
    return {
      name: "",
      children: traverse(content),
    };
  } catch (error) {
    console.error("Error in convertToNestedStructure:", error);
    return {
      name: "",
      children: [],
    };
  }
};

function traverse(node) {
  try {
    if (Array.isArray(node)) {
      // Handle arrays (e.g., files or mixed arrays of files and folders)
      return node.map((item) => traverse(item)).flat();
    } else if (typeof node === "object" && node !== null) {
      // Handle objects (directories)
      return Object.entries(node).map(([key, value]) => ({
        name: key,
        children: traverse(value), // Recursively process children
      }));
    } else if (typeof node === "string") {
      // Handle string leaf nodes (files)
      return { name: node };
    }
    return []; // Return an empty array for unexpected input types
  } catch (error) {
    console.error("Error in traverse:", error);
    return [];
  }
}

export const searchTree = (tree, query) => {
  if (!query) return [tree]; // If no query, return the full tree wrapped in an array

  const filterTree = (node) => {
    // Check if the node's name includes the query string
    const isMatch = node.name.toLowerCase().includes(query.toLowerCase());

    // If the node has children, recursively filter them
    if (node.children && node.children.length > 0) {
      const filteredChildren = node.children
        .map(filterTree) // Recursively filter children
        .filter((child) => child !== null); // Remove nulls

      // If the node itself matches or has matching children, include it
      if (isMatch || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren, isExpanded: true };
      }
    }

    // If the node is a leaf and matches, include it
    if (isMatch) {
      return { ...node };
    }

    // Otherwise, exclude the node
    return null;
  };

  // Filter the root node
  const filteredRoot = filterTree(tree);

  // Return the filtered root as an array (since the root is a single node)
  return filteredRoot ? [filteredRoot] : [];
};

export const findFilesByKey = (
  node,
  type = "archive",
  path = [],
  isUnderContent = false,
) => {
  let results = [];

  // Skip if we're under a content folder
  if (isUnderContent) {
    return results;
  }

  // Handle arrays
  if (Array.isArray(node)) {
    node.forEach((child) => {
      results = results.concat(
        findFilesByKey(child, type, path, isUnderContent),
      );
    });
  }
  // Handle objects
  else if (typeof node === "object" && node !== null) {
    for (const key in node) {
      const newPath = [...path, key];

      // Check if we're entering a content section
      const checkingContent = key === "content";

      // Check if this is the archive key we're looking for
      if (key === type && !isUnderContent) {
        const buildArchivePath = (uuid, relPath) => {
          const basePath = newPath.join("/");
          const nestedArchiveMarker = `${type}/`;

          // Newer payloads can accidentally include region/stage/archive under
          // the archive branch. Trim to the last archive segment so download URLs
          // still point at .../archive/<guid>/<file>.
          if (relPath.includes(nestedArchiveMarker)) {
            const baseBeforeArchive =
              newPath.at(-1) === type
                ? newPath.slice(0, -1).join("/")
                : basePath;
            const afterArchive = relPath.slice(
              relPath.lastIndexOf(nestedArchiveMarker) +
                nestedArchiveMarker.length,
            );
            return `${baseBeforeArchive}/${type}/${afterArchive}`;
          }

          return `${basePath}/${uuid}/${relPath}`;
        };

        const pushArchiveLeaves = (files, uuid, relSegments = []) => {
          if (typeof files === "string") {
            const relPath = relSegments.length
              ? `${relSegments.join("/")}/${files}`
              : files;

            results.push({
              file: files,
              uuid,
              path: buildArchivePath(uuid, relPath),
            });
            return;
          }

          if (!Array.isArray(files)) {
            if (files && typeof files === "object") {
              Object.entries(files).forEach(([folderName, nested]) => {
                pushArchiveLeaves(nested, uuid, [...relSegments, folderName]);
              });
            }
            return;
          }

          files.forEach((entry) => {
            if (typeof entry === "string") {
              const relPath = relSegments.length
                ? `${relSegments.join("/")}/${entry}`
                : entry;

              results.push({
                file: entry,
                uuid,
                path: buildArchivePath(uuid, relPath),
              });
              return;
            }

            if (entry && typeof entry === "object" && !Array.isArray(entry)) {
              Object.entries(entry).forEach(([folderName, nested]) => {
                pushArchiveLeaves(nested, uuid, [...relSegments, folderName]);
              });
            }
          });
        };

        const archiveItems = Array.isArray(node[key]) ? node[key] : [node[key]];
        archiveItems.forEach((item) => {
          if (!item || typeof item !== "object") return;
          Object.entries(item).forEach(([uuid, files]) => {
            pushArchiveLeaves(files, uuid);
          });
        });
        continue;
      }

      // Continue searching in nested objects/arrays
      // Pass the content flag if we're inside a content section
      results = results.concat(
        findFilesByKey(
          node[key],
          type,
          newPath,
          isUnderContent || checkingContent,
        ),
      );
    }
  }

  return results;
};
